import {html, css, TemplateResult} from 'lit'
import {classMap} from 'lit-html/directives/class-map.js'
import {customElement, property} from 'lit/decorators.js'
import {BaseComponent} from './base-component.js'

export enum ADJUST_OPTIONS {
  height = 'height',
  width = 'width'
}

@customElement('il-image')
export default class ImageComponent extends BaseComponent {
  static override styles = [
    BaseComponent.styles,
    css`
      .il-image {
        position: relative;
        overflow: hidden;
        font-size: 0;
        display: flex;
        align-items: center;
        justify-content: center;
      }

      .il-image.il-image img {
        object-fit: cover;
        height: auto;
        width: 100%;
      }

      .il-image.il-image--absolute img {
        position: absolute;
        top: 50%;
        left: 50%;
        transform: translate(-50%, -50%);
      }

      .il-image.il-image--contain-y img {
        width: auto;
        height: 100%;
      }
    `,
  ]

  /**
   * The name of the image. Used to get path for internal images.
   */
  @property({type: String})
  name: string = ''

  /**
   * The full path for the image. Used for external images.
   */
  @property({type: String})
  src: string = ''

  /**
   * The alternative text to put in case the image fails. A11y property.
   */
  @property({type: String})
  alt: string = ''

  /**
   * Height of the image. Needed to reserve space while loading the page.
   */
  @property({type: Number})
  height: number = 0

  /**
   * Width of the image. Needed to reserve space while loading the page.
   */
  @property({type: Number})
  width: number = 0

  /**
   * Adjusts the image to be contained in height or width
   */
  @property({attribute: 'adjust-to', type: String})
  adjustTo?: ADJUST_OPTIONS = undefined

  /**
   * Expands the container of the image to the width of the parent element.
   */
  @property({attribute: 'full-width', type: Boolean})
  fullWidth: boolean = false

  private imageContainerClasses (): {
    [x: string]: boolean
  } {
    return {
      [`${this.baseCSSClass} il-image`]: true,
      // Image uses position: absolute in order to calculate the aspect ratio
      'il-image--absolute': !!this.width && !!this.height,
      'il-image--contain-y': !this.onlyHeightPassed && this.shouldFixToHeight(),
    }
  }

  override render (): TemplateResult {
    return html`
      <div
        class="${this.baseCSSClass}"
        style="width: ${this.getContainerWidth()}"
        data-test-iliad-image-container
      >
        <div
          class="${classMap(this.imageContainerClasses())}"
          style="${this.getContainerStyles()}"
          data-test-iliad-image-content
        >
          <img
            src="${this.src}"
            alt="${this.alt}"
            height="${this.height}"
            width="${this.width}"
            data-test-iliad-image
          />
        </div>
      </div>
    `
  }

  private getContainerStyles (): string {
    let styles = ''
    if (this.width && this.height) {
      /**
       * Padding bottom is the inverse of the aspect ratio.
       * * Aspect ratio: width/height
       * * Padding bottom: height/width * 100
       */
      const paddingBottom = ((this.height / this.width) * 100).toFixed(2)
      styles = `padding-bottom: ${paddingBottom}%;`
    } else if (this.height) {
      styles = `height: ${this.height}px;`
    } else if (this.width) {
      styles = `width: ${this.width}px;`
    }

    return styles
  }

  private getContainerWidth (): string {
    if (!this.width || this.fullWidth) {
      return `100%`
    }

    return `${this.width}px;`
  }

  private shouldFixToHeight (): boolean {
    const _fixToHeight =
      this.width < this.height || this.adjustTo === ADJUST_OPTIONS.height

    return _fixToHeight && this.adjustTo !== ADJUST_OPTIONS.width
  }

  private get onlyHeightPassed (): boolean {
    return !this.width && !!this.height
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'il-image': ImageComponent
  }
}
